import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaArrowLeft, FaGlobeAmericas, FaTools, FaWallet, FaSyncAlt } from 'react-icons/fa';
import { useData } from '../contexts/DataContext';

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 4rem 1.5rem 4rem;
  background: linear-gradient(150deg, rgba(5, 10, 24, 1) 0%, rgba(10, 18, 36, 1) 50%, rgba(4, 8, 18, 1) 100%);
  color: #ffffff;
  position: relative;
  overflow-x: hidden;

  @media (max-width: 1150px) {
    padding-top: 5.5rem;
    padding-left: 1rem;
    padding-right: 1rem;
  }

  @media (max-width: 900px) {
    padding-top: 4.5rem;
    padding-left: 1rem;
    padding-right: 1rem;
  }

  @media (max-width: 768px) {
    padding: 4rem 1rem 3rem;
  }

  @media (max-width: 480px) {
    padding: 3.5rem 0.75rem 2rem;
  }
`;

const LandingButton = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.55rem 1.2rem;
  border-radius: 999px;
  background: rgba(10, 18, 36, 0.8);
  border: 1px solid rgba(64, 224, 255, 0.45);
  color: rgba(255, 255, 255, 0.92);
  text-decoration: none;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  transition: all 0.3s ease;
  z-index: 10;
  white-space: nowrap;

  &:hover {
    background: rgba(64, 224, 255, 0.18);
    transform: translateY(-2px);
    color: #ffffff;
  }

  @media (max-width: 1150px) {
    padding: 0.45rem 1rem 0.45rem 0.7rem;
    font-size: 0.9rem;
    gap: 0.5rem;
    left: 1rem;
    top: 1.5rem;
  }

  @media (max-width: 900px) {
    position: relative;
    top: auto;
    left: auto;
    align-self: flex-start;
    margin-bottom: 1.5rem;
    padding: 0.5rem 1rem 0.5rem 0.75rem;
    font-size: 0.85rem;
  }

  @media (max-width: 480px) {
    padding: 0.45rem 0.9rem 0.45rem 0.7rem;
    font-size: 0.8rem;
    margin-bottom: 1rem;
  }
`;

const LandingIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(135deg, #40e0ff, #00bfff);
  color: #081224;
  font-size: 0.9rem;
  box-shadow: 0 6px 16px rgba(64, 224, 255, 0.35);
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-12px); }
`;

const BackgroundOrb = styled.div<{ $size: number; $top: string; $left: string; $delay?: number }>`
  position: absolute;
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  top: ${props => props.$top};
  left: ${props => props.$left};
  background: radial-gradient(circle, rgba(64, 224, 255, 0.25) 0%, rgba(64, 224, 255, 0) 70%);
  filter: blur(45px);
  opacity: 0.6;
  pointer-events: none;
  animation: ${float} 9s ease-in-out infinite;
  animation-delay: ${props => props.$delay || 0}s;
`;

const Card = styled.div`
  max-width: 1120px;
  width: 100%;
  background: rgba(15, 24, 48, 0.78);
  border: 1px solid rgba(64, 224, 255, 0.25);
  border-radius: 26px;
  padding: 3.5rem 3.5rem;
  box-shadow: 0 35px 70px rgba(6, 10, 25, 0.55);
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  margin-top: 0;

  @media (min-width: 1151px) {
    margin-top: 4rem;
  }

  @media (max-width: 1150px) {
    margin-top: 0;
  }

  @media (max-width: 900px) {
    padding: 2.5rem 2rem;
    border-radius: 20px;
    gap: 1.4rem;
    margin-top: 0;
  }

  @media (max-width: 640px) {
    padding: 2rem 1.5rem;
  }

  @media (max-width: 480px) {
    padding: 1.75rem 1.25rem;
    border-radius: 18px;
    gap: 1.2rem;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 20% 20%, rgba(64, 224, 255, 0.12), transparent 60%),
                radial-gradient(circle at 80% 0%, rgba(64, 224, 255, 0.1), transparent 65%);
    opacity: 0.8;
    pointer-events: none;
  }
`;

const Heading = styled.h1`
  margin: 0;
  font-size: clamp(2.6rem, 5vw, 3.4rem);
  font-weight: 200;
  letter-spacing: -0.03em;
`;

type ServiceVariant = 'build' | 'manage' | 'pricing' | 'improve';

const variantStyles: Record<ServiceVariant, { background: string; border: string; iconBg: string; iconShadow: string; iconColor: string }> = {
  build: {
    background: 'linear-gradient(135deg, rgba(64, 224, 255, 0.16), rgba(64, 224, 255, 0.02))',
    border: 'rgba(64, 224, 255, 0.28)',
    iconBg: 'linear-gradient(135deg, #40e0ff, #64c8ff)',
    iconShadow: '0 12px 24px rgba(64, 224, 255, 0.25)',
    iconColor: '#041427'
  },
  manage: {
    background: 'linear-gradient(135deg, rgba(148, 42, 255, 0.18), rgba(64, 224, 255, 0.05))',
    border: 'rgba(148, 42, 255, 0.32)',
    iconBg: 'linear-gradient(135deg, #a855f7, #6366f1)',
    iconShadow: '0 12px 24px rgba(148, 42, 255, 0.25)',
    iconColor: '#120826'
  },
  pricing: {
    background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.18), rgba(64, 224, 255, 0.04))',
    border: 'rgba(255, 193, 7, 0.32)',
    iconBg: 'linear-gradient(135deg, #ffce52, #f59e0b)',
    iconShadow: '0 12px 24px rgba(255, 193, 7, 0.25)',
    iconColor: '#2b1600'
  },
  improve: {
    background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.18), rgba(64, 224, 255, 0.04))',
    border: 'rgba(34, 197, 94, 0.32)',
    iconBg: 'linear-gradient(135deg, #34d399, #10b981)',
    iconShadow: '0 12px 24px rgba(34, 197, 94, 0.25)',
    iconColor: '#022812'
  }
};

const Points = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(320px, 1fr));
  justify-content: center;
  gap: 1.8rem 2.4rem;
  max-width: 900px;
  margin: 0 auto;
  text-align: left;

  @media (max-width: 900px) {
    grid-template-columns: minmax(280px, 1fr);
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    gap: 1.2rem;
  }
`;

const Point = styled.div<{ $variant: ServiceVariant }>`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  background: ${({ $variant }) => variantStyles[$variant].background};
  border: 1px solid ${({ $variant }) => variantStyles[$variant].border};
  border-radius: 16px;
  padding: 1.8rem 2rem;
  backdrop-filter: blur(6px);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-4px);
  }

  @media (max-width: 480px) {
    padding: 1.4rem 1.5rem;
    border-radius: 14px;
    gap: 0.7rem;
  }
`;

const PointIcon = styled.div<{ $variant: ServiceVariant }>`
  align-self: flex-start;
  width: 54px;
  height: 54px;
  border-radius: 14px;
  background: ${({ $variant }) => variantStyles[$variant].iconBg};
  border: none;
  color: ${({ $variant }) => variantStyles[$variant].iconColor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  box-shadow: ${({ $variant }) => variantStyles[$variant].iconShadow};

  svg {
    width: 24px;
    height: 24px;
  }
`;

const PointContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

const PointHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const PointTitle = styled.h2`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #ffffff;

  @media (max-width: 480px) {
    font-size: 0.95rem;
  }
`;

const PointCopy = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.98rem;
  line-height: 1.55;

  @media (max-width: 480px) {
    font-size: 0.9rem;
    line-height: 1.5;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0.95rem 2.6rem;
  border-radius: 999px;
  background: linear-gradient(135deg, rgba(64, 224, 255, 0.3), rgba(64, 224, 255, 0.16));
  border: 1px solid rgba(64, 224, 255, 0.45);
  color: #ffffff;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 22px 42px rgba(64, 224, 255, 0.28);
  }

  @media (max-width: 768px) {
    padding: 0.85rem 2rem;
    font-size: 0.95rem;
  }

  @media (max-width: 480px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
    gap: 0.5rem;
    white-space: normal;
    text-align: center;
    justify-content: center;
    flex-wrap: wrap;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 1120px;
  gap: 1.5rem;

  @media (max-width: 900px) {
    gap: 1rem;
  }
`;

const WebServicesPage: React.FC = () => {
  const { profile } = useData();
  return (
    <Wrapper>
      <BackgroundOrb $size={420} $top="-8%" $left="15%" />
      <BackgroundOrb $size={360} $top="65%" $left="75%" $delay={1.5} />
      <BackgroundOrb $size={280} $top="80%" $left="10%" $delay={0.8} />
      <ContentWrapper>
        <LandingButton to="/">
          <LandingIcon>
            <FaArrowLeft />
          </LandingIcon>
          <span>ollie-gillyon.com</span>
        </LandingButton>
        <Card>
        <Heading>Website Services</Heading>
        <Points>
          <Point $variant="build">
            <PointHeader>
              <PointIcon $variant="build"><FaGlobeAmericas /></PointIcon>
              <PointTitle>Simple website build</PointTitle>
            </PointHeader>
            <PointContent>
              <PointCopy>
                Clean and simple website to attract users to your business
              </PointCopy>
            </PointContent>
          </Point>
          <Point $variant="manage">
            <PointHeader>
              <PointIcon $variant="manage"><FaTools /></PointIcon>
              <PointTitle>Hassle-free management</PointTitle>
            </PointHeader>
            <PointContent>
              <PointCopy>
                Hosting, security, management and updates handled for you, so you can focus on your business
              </PointCopy>
            </PointContent>
          </Point>
          <Point $variant="improve">
            <PointHeader>
              <PointIcon $variant="improve"><FaSyncAlt /></PointIcon>
              <PointTitle>Continuous improvements</PointTitle>
            </PointHeader>
            <PointContent>
              <PointCopy>
                Need new pages, content refreshes or tweaks? I iterate with you and keep the site evolving
              </PointCopy>
            </PointContent>
          </Point>
          <Point $variant="pricing">
            <PointHeader>
              <PointIcon $variant="pricing"><FaWallet /></PointIcon>
              <PointTitle>Simple & affordable pricing</PointTitle>
            </PointHeader>
            <PointContent>
              <PointCopy>
                One clear annual fee keeps things running, with straightforward hourly tweaks when you want improvements - no complicated retainer
              </PointCopy>
            </PointContent>
          </Point>
        </Points>

        <ButtonRow>
          <CTAButton href={profile.email ? `mailto:${profile.email}` : 'mailto:'}>
            Email me at {profile.email || 'ollie_gillyon@outlook.com'} <FaArrowRight />
          </CTAButton>
        </ButtonRow>
        </Card>
      </ContentWrapper>
    </Wrapper>
  );
};

export default WebServicesPage;
